/**
 * Surplus de projet et dispositions — les appels (V1.6-10B).
 *
 * Trois règles, les mêmes que depuis V1.6-09 :
 *
 * 1. **aucun corps de requête ne porte d'argent.** Un surplus est une intention
 *    et des quantités ; ce qu'il vaut ne regarde pas ce module ;
 * 2. **l'écran ne décide d'aucun droit.** `can_open`, `can_decide`,
 *    `can_finalize` viennent du serveur ; un bouton grisé est une politesse,
 *    pas une autorisation ;
 * 3. **l'écran ne calcule jamais ce qui reste.** Déclaré, disposé, reste
 *    arrivent calculés, dans une seule unité, et le serveur les revérifie sous
 *    verrou au moment d'exécuter.
 *
 * `staleTime: 0` : ce qui change un surplus se passe ailleurs — un collègue
 * exécute une décision, un camion part — et une fiche servie depuis un cache
 * tiède ferait décider deux fois de la même marchandise.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "./client";
import type {
  ProjectSurplusCreateInput,
  ProjectSurplusDetail,
  ProjectSurplusPage,
  ProjectSurplusStatus,
  ReturnableReceipt,
  SurplusDispositionInput,
  SurplusLineInput,
  Uuid,
} from "./types";

export const surplusKeys = {
  all: ["project-surpluses"] as const,
  list: (params: object) => ["project-surpluses", "list", params] as const,
  one: (surplusId: Uuid) => ["project-surpluses", surplusId] as const,
  sources: (surplusId: Uuid, lineId: Uuid) =>
    ["project-surpluses", surplusId, "lines", lineId, "returnable-receipts"] as const,
};

export interface SurplusParams {
  [key: string]: unknown;
  project_id?: Uuid;
  status?: ProjectSurplusStatus;
  limit?: number;
  offset?: number;
}

function query(path: string, params?: Record<string, unknown>) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined && value !== null && value !== "") {
      search.set(key, String(value));
    }
  }
  const suffix = search.toString();
  return suffix ? `${path}?${suffix}` : path;
}

export function useProjectSurpluses(params: SurplusParams = {}) {
  return useQuery({
    queryKey: surplusKeys.list(params),
    queryFn: () => apiFetch<ProjectSurplusPage>(query("/project-surpluses", params)),
    staleTime: 0,
  });
}

export function useProjectSurplus(surplusId: Uuid | undefined) {
  return useQuery({
    queryKey: surplusKeys.one(surplusId ?? ""),
    queryFn: () => apiFetch<ProjectSurplusDetail>(`/project-surpluses/${surplusId}`),
    enabled: Boolean(surplusId),
    staleTime: 0,
  });
}

/**
 * D'où cette marchandise peut repartir chez un fournisseur (V1.6-10B.1).
 *
 * **L'écran ne choisit pas et ne classe pas.** Le serveur rend les réceptions
 * admissibles dans un ordre stable ; l'utilisateur en désigne une. Une liste
 * vide est une réponse : il n'y a pas de retour fournisseur possible pour cet
 * article, et l'écran doit le dire plutôt que d'offrir un geste qui serait
 * refusé.
 *
 * `enabled` : la liste n'est demandée que lorsque quelqu'un a choisi « retour
 * fournisseur ». La charger d'avance ferait une requête à chaque ouverture de
 * la boîte, pour une décision qui n'est prise qu'une fois sur quatre.
 */
export function useReturnableReceipts(
  surplusId: Uuid | undefined,
  lineId: Uuid | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: surplusKeys.sources(surplusId ?? "", lineId ?? ""),
    queryFn: () =>
      apiFetch<ReturnableReceipt[]>(
        `/project-surpluses/${surplusId}/lines/${lineId}/returnable-receipts`,
      ),
    enabled: enabled && Boolean(surplusId) && Boolean(lineId),
    staleTime: 0,
  });
}

function settle(
  queryClient: ReturnType<typeof useQueryClient>,
  detail: ProjectSurplusDetail,
  { movedStock = false }: { movedStock?: boolean } = {},
) {
  queryClient.setQueryData(surplusKeys.one(detail.surplus.id), detail);
  void queryClient.invalidateQueries({ queryKey: surplusKeys.all });
  void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  if (movedStock) {
    // Une exécution a bougé du stock : les écrans d'inventaire mentent jusqu'à
    // ce qu'ils relisent.
    void queryClient.invalidateQueries({ queryKey: ["inventory"] });
    void queryClient.invalidateQueries({ queryKey: ["supplier-returns"] });
    // Un retour exécuté consomme une source : la proposer encore ferait choisir
    // une réception qui n'a plus rien à renvoyer.
    void queryClient.invalidateQueries({ queryKey: surplusKeys.all });
  }
}

/** Noter ce qui reste sur un chantier. **Aucun stock ne bouge.** */
export function useDeclareSurplus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ProjectSurplusCreateInput) =>
      apiFetch<ProjectSurplusDetail>("/project-surpluses", { method: "POST", body }),
    onSuccess: (data) => settle(queryClient, data),
  });
}

export function useUpdateSurplus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ surplusId, lines }: { surplusId: Uuid; lines: SurplusLineInput[] }) =>
      apiFetch<ProjectSurplusDetail>(`/project-surpluses/${surplusId}`, {
        method: "PATCH",
        body: { lines },
      }),
    onSuccess: (data) => settle(queryClient, data),
  });
}

/** Confirmer la déclaration. **Toujours aucun mouvement de stock.** */
export function useOpenSurplus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ surplusId }: { surplusId: Uuid }) =>
      apiFetch<ProjectSurplusDetail>(`/project-surpluses/${surplusId}/open`, {
        method: "POST",
      }),
    onSuccess: (data) => settle(queryClient, data),
  });
}

export function useCancelSurplus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ surplusId, reason }: { surplusId: Uuid; reason?: string | null }) =>
      apiFetch<ProjectSurplusDetail>(`/project-surpluses/${surplusId}/cancel`, {
        method: "POST",
        body: { reason: reason || null },
      }),
    onSuccess: (data) => settle(queryClient, data),
  });
}

/** Décider quoi faire d'une partie du surplus. **Aucun stock ne bouge.** */
export function useCreateDisposition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ surplusId, ...body }: { surplusId: Uuid } & SurplusDispositionInput) =>
      apiFetch<ProjectSurplusDetail>(`/project-surpluses/${surplusId}/dispositions`, {
        method: "POST",
        body,
      }),
    onSuccess: (data) => settle(queryClient, data),
  });
}

/**
 * Exécuter la décision. **Le seul chemin vers le grand livre.**
 *
 * Un geste, et un seul : un retour fournisseur délègue au retour lui-même et
 * n'écrit aucun mouvement de son côté.
 */
export function useFinalizeDisposition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ surplusId, dispositionId }: { surplusId: Uuid; dispositionId: Uuid }) =>
      apiFetch<ProjectSurplusDetail>(
        `/project-surpluses/${surplusId}/dispositions/${dispositionId}/finalize`,
        { method: "POST" },
      ),
    onSuccess: (data) => settle(queryClient, data, { movedStock: true }),
  });
}

export function useCancelDisposition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ surplusId, dispositionId }: { surplusId: Uuid; dispositionId: Uuid }) =>
      apiFetch<ProjectSurplusDetail>(
        `/project-surpluses/${surplusId}/dispositions/${dispositionId}/cancel`,
        { method: "POST" },
      ),
    onSuccess: (data) => settle(queryClient, data),
  });
}
